import { computed, ComputedRef } from '@vue/composition-api'
import { StatusColor } from '../components/RegionSummary/WRTCServerSummary'
import { WRTCServerType } from '@/types/admin'
import { WrtcServerStat } from '../types'
import { JANUS_CONNECTION_LIMIT, WOWZA_CONNECTION_LIMIT, WOWZA_THREAD_LIMIT } from '../classes/WrtcServerStat'


const sizeSummary = (free: number, used: number): string => {
  let percent = free / (used + free) * 100
  if (Number.isNaN(percent)) percent = 0
  const percentStr = formatNumber(percent)
  return `${formatSize(used)} (${percentStr}% free)`
}

const formatSize = (value: number): string => formatNumber(value / 1024 / 1024 / 1024) + 'GB'

const formatNumber = (value: number): string => '' + (Math.round(value * 10) / 10)

const getConnectionCount = (stats: WrtcServerStat): number => {
  if (stats.serverType === WRTCServerType.JANUS) {
    return stats.subscriberCount + stats.publisherCount
  } else {
    return stats.connectionCount
  }
}

const useWrtcStatsToString = (stats: ComputedRef<WrtcServerStat|undefined>) => {
  const connections = computed(() => {
    if (!stats.value) return 'unknown'
    return '' + getConnectionCount(stats.value)
  })

  const connectionColor = computed(()=> {
    if (!stats.value) return StatusColor.GRAY
    const connectionCount = getConnectionCount(stats.value)
    if (stats.value.serverType === WRTCServerType.JANUS) {
      return connectionCount > JANUS_CONNECTION_LIMIT ? StatusColor.RED : StatusColor.GREEN
    } else {
      return connectionCount > WOWZA_CONNECTION_LIMIT ? StatusColor.RED : StatusColor.GREEN
    }
  })

  const threads = computed(() => {
    if (stats.value?.serverType === WRTCServerType.JANUS) {
      return 'not supported'
    }
    return  '' + (stats.value?.threadCount ?? 'unknown')
  })
  const threadsColor = computed(() => {
    if (stats.value?.serverType === WRTCServerType.JANUS) {
      return StatusColor.GRAY
    }
    if (stats.value?.threadCount === undefined) return StatusColor.GRAY
    if (stats.value.threadCount === 0) return StatusColor.GRAY
    return stats.value.threadCount > WOWZA_THREAD_LIMIT ? StatusColor.RED : StatusColor.GREEN
  })
  const cpu = computed(() => {
    if (stats.value?.serverType === WRTCServerType.JANUS) {
      return `Service: ${stats.value.cpuUsage}`
    }
    if (stats.value === undefined) return 'unknown'
    return `Service: ${stats.value.cpuUser}% / System: ${stats.value.cpuSystem}%`
  })
  // eslint-disable-next-line complexity
  const cpuColor = computed(() => {
    if (stats.value?.serverType === WRTCServerType.JANUS) {
      if (stats.value.cpuUsage === 0) return StatusColor.GRAY
      return stats.value.cpuUsage > 70 ? StatusColor.RED : StatusColor.GREEN
    }
    if (stats.value === undefined) return StatusColor.GRAY
    if (stats.value.cpuIdle === 0 && stats.value.cpuUser === 0 && stats.value.cpuSystem === 0) return StatusColor.GRAY
    return stats.value.cpuIdle > 30 ? StatusColor.GREEN : StatusColor.RED
  })
  const memory = computed(() => {
    if (stats.value === undefined) return 'unknown'
    if (stats.value.serverType === WRTCServerType.JANUS) {
      return 'not supported'
    }
    return sizeSummary(stats.value.memoryFree, stats.value.memoryUsed)
  })
  const memoryColor = computed(() => {
    if (stats.value === undefined) return StatusColor.GRAY
    if (stats.value?.serverType === WRTCServerType.JANUS) {
      return StatusColor.GRAY
    }
    if (stats.value.memoryFree === 0 && stats.value.memoryUsed === 0) return StatusColor.GRAY
    const percent = stats.value.memoryFree / (stats.value.memoryFree + stats.value.memoryUsed) * 100
    return percent > 15 ? StatusColor.GREEN : StatusColor.RED
  })
  const heap = computed(() => {
    if (stats.value === undefined) return 'unknown'
    if (stats.value?.serverType === WRTCServerType.JANUS) {
      return 'not supported'
    }
    return sizeSummary(stats.value.heapFree, stats.value.heapUsed)
  })
  const heapColor = computed(() => {
    if (stats.value === undefined) return StatusColor.GRAY
    if (stats.value?.serverType === WRTCServerType.JANUS) {
      return StatusColor.GRAY
    }
    if (stats.value.heapFree === 0 && stats.value.heapUsed === 0) return StatusColor.GRAY
    const percent = stats.value.heapFree / (stats.value.heapFree + stats.value.heapUsed) * 100
    return percent > 15 ? StatusColor.GREEN : StatusColor.RED
  })
  const disk = computed(() => {
    if (stats.value === undefined) return 'unknown'
    if (stats.value?.serverType === WRTCServerType.JANUS) {
      return 'not supported'
    }
    return sizeSummary(stats.value.diskFree, stats.value.diskUsed)
  })
  const diskColor = computed(() => {
    if (stats.value === undefined) return StatusColor.GRAY
    if (stats.value?.serverType === WRTCServerType.JANUS) {
      return StatusColor.GRAY
    }
    if (stats.value.diskFree === 0 && stats.value.diskUsed === 0) return StatusColor.GRAY
    const percent = stats.value.diskFree / (stats.value.diskFree + stats.value.diskUsed) * 100
    return percent > 15 ? StatusColor.GREEN : StatusColor.RED
  })

  return {
    connections,
    connectionColor,
    threads,
    threadsColor,
    cpu,
    cpuColor,
    memory,
    memoryColor,
    heap,
    heapColor,
    disk,
    diskColor
  }
}

export default useWrtcStatsToString
